import React from "react";
import { ScheduleMapping, Break } from "../api/models";
import { DoctorLabel } from "./DoctorLabel";
import { ValidationView } from "./ValidationView";
import { modelValidator } from "./modelValidator";
import { Button, ButtonGroup, Grid, GridItem, formatDate, groupBy } from "@optimed/react";
import { Badge, OperationButton, toast } from "@optimed/react";
import { SlotLookup } from "./SlotLookup";
import { Identifier } from "./Identifier";
import { syncService } from "../api";
import { scheduleSettingsDialog } from "./ScheduleSettingsDialog";

interface ScheduleViewProps extends React.HTMLAttributes<HTMLDivElement> {
  schedule: ScheduleMapping;
}

export function ScheduleView(props: ScheduleViewProps) {
  const { schedule, ...rootAttrs } = props;
  const docplannerSlots = schedule.docplanner?.slots ?? [];
  const docplannerGroups = groupBy(docplannerSlots, x => formatDate(x.start, "yyyy-MM-dd"));
  const docplannerBreaks = schedule.docplanner?.breaks ?? [];
  const hisSlots = schedule.his?.slots ?? [];
  const hisGroups = groupBy(hisSlots, x => formatDate(x.od, "yyyy-MM-dd"));
  const slotLookup = new SlotLookup(docplannerSlots, hisSlots);

  return (
    <div {...rootAttrs}>
      <Grid contentHeight>
        <GridItem colTemplate="1fr">
          <DoctorLabel doctor={schedule.doctorMapping!} />
        </GridItem>
        <GridItem colTemplate="auto">
          <ButtonGroup sm>
            <OperationButton
              text="Synchronizuj"
              icon="sync-alt"
              outline
              operation={() => syncSchedule()}
              title="Wykonaj ręczną synchronizacje grafiku"
              disabled={!schedule.doctorMapping?.his || !schedule.doctorMapping?.docplanner}
            />
            <Button
              icon="gears"
              onClick={() => scheduleSettingsDialog.open({ doctor: schedule.doctorMapping! })}
              title="Ustawienia grafiku"
            />
          </ButtonGroup>
        </GridItem>
      </Grid>
      <ValidationView validations={modelValidator.checkScheduleMapping(schedule)} />
      <Grid className="small">
        <GridItem colTemplate="1fr">
          <div>Terminy w portalu Znany Lekarz</div>
          <div hidden={docplannerGroups.length > 0}>Brak terminów</div>
          {docplannerGroups.map(x => (
            <div key={x.keyHash}>
              <div>{x.key}</div>
              {x.items.map(s => (
                <span key={s.start}>
                  {" "}
                  <Badge
                    type={slotLookup.existsInHis(s.start) ? "secondary" : "danger"}
                    text={formatDate(s.start, "HH:mm")}
                  />
                </span>
              ))}
            </div>
          ))}
          <div hidden={!docplannerBreaks.length}>
            <div>Blokady w portalu Znany Lekarz</div>
            {docplannerBreaks.map(x => renderBreak(x))}
          </div>
        </GridItem>
        <GridItem colTemplate="1fr">
          <div>Terminy w Optimed NXT</div>
          <div hidden={hisGroups.length > 0}>Brak terminów</div>
          {hisGroups.map(x => (
            <div key={x.keyHash}>
              <div>{x.key}</div>
              {x.items.map(s => (
                <span key={s.dostepnoscId}>
                  {" "}
                  <Badge
                    type={slotLookup.existsInDocplanner(s.od) ? "secondary" : "warning"}
                    text={formatDate(s.od, "HH:mm")}
                  />
                </span>
              ))}
            </div>
          ))}
        </GridItem>
      </Grid>
    </div>
  );

  function renderBreak(x: Break) {
    let sinceFormat = "yyyy-MM-dd HH:mm";
    let tillFormat =
      formatDate(x.since, "yyyy-MM-dd") === formatDate(x.till, "yyyy-MM-dd") ? "HH:mm" : "yyyy-MM-dd HH:mm";
    return (
      <div key={x.id}>
        {formatDate(x.since, sinceFormat)} - {formatDate(x.till, tillFormat)}{" "}
        <Identifier value={x.id} title="Identyfikator blokady" />
      </div>
    );
  }

  function syncSchedule() {
    let hisDoctorId = schedule.doctorMapping?.his?.doctor?.id;
    let hisUnitId = schedule.doctorMapping?.his?.unit?.id;

    if (!hisDoctorId || !hisUnitId) {
      toast.error("Nie można wywołać synchronizacji dla tego grafiku, brak mapowania");
      return Promise.reject();
    }

    return syncService
      .scheduleForHisDoctor({ hisDoctorId, hisUnitId, execute: true })
      .then(() => toast.success("Synchronizacja grafiku została zakończona, odśwież stronę aby zobaczyć zmiany"));
  }
}
